import { useNavigate, useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { msgTypes } from "../../constants/msgTypes";
import { useGlobalState } from "../../services/context/AuthContext";
import {
  conditionalCodes,
  impliedPermissions,
  MENU_CONFIG,
} from "../../constants/menuItem";
import { extractAllowedCodes } from "../../constants/helper";
import { Role } from "../../constants/enum";
import ApiUrl from "../../services/shared/ApiUrl";

interface Feature {
  label: string;
  path: string;
  code: string;
  children?: Feature[];
}

const SideMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { permission, userInfo } = useGlobalState();

  const userRole = userInfo?.roleName;
  const isSuperAdmin = userRole === Role.SUPER_ADMIN;
  const allowedCodes = extractAllowedCodes(
    permission || [],
    impliedPermissions,
    conditionalCodes
  );

  const [expandedSections, setExpandedSections] = useState<string | null>(
    () => {
      return localStorage.getItem("sidebar_expanded_section");
    }
  );

  const [openSubMenu, setOpenSubMenu] = useState<string | null>(() => {
    return localStorage.getItem("sidebar_open_submenu");
  });

  useEffect(() => {
    if (location.pathname) {
      MENU_CONFIG.forEach((section, index) => {
        const sectionKey = `main-${index}`;
        section.children?.forEach((item: any) => {
          if (item.path === location.pathname) {
            setExpandedSections(sectionKey);
            localStorage.setItem("sidebar_expanded_section", sectionKey);
          }
          item.children?.forEach((child: { path: string }) => {
            if (child.path === location.pathname) {
              setExpandedSections(sectionKey);
              setOpenSubMenu(`${sectionKey}-${item.code}`);
              localStorage.setItem("sidebar_expanded_section", sectionKey);
              localStorage.setItem(
                "sidebar_open_submenu",
                `${sectionKey}-${item.code}`
              );
            }
          });
        });
      });
    }
  }, [location.pathname]);

  const toggleMainSection = (key: string) => {
    const newSection = expandedSections === key ? null : key;
    setExpandedSections(newSection);
    localStorage.setItem("sidebar_expanded_section", newSection || "");
  };

  const toggleSubMenu = (key: string) => {
    const newSub = openSubMenu === key ? null : key;
    setOpenSubMenu(newSub);
    localStorage.setItem("sidebar_open_submenu", newSub || "");
  };

  const renderMenuItems = (items: Feature[], sectionKey: string) => {
    return (
      isSuperAdmin
        ? items
        : items.filter(
            (item) =>
              allowedCodes.includes(item.code) ||
              item.children?.some((child) => allowedCodes.includes(child.code))
          )
    )?.map((item, index) => {
      const itemKey = `${sectionKey}-${item.code}`;
      const isOpen = openSubMenu === itemKey;
      const hasChildren = isSuperAdmin
        ? !!item.children && item.children.length > 0
        : item.children?.some((child) => allowedCodes.includes(child.code));

      return (
        <div
          key={index}
          className="menu-accordion gap-0.5 ps-[10px] relative before:absolute before:start-[20px] before:top-0 before:bottom-0 before:border-s before:border-gray-200 show"
        >
          <div className="menu-item menu-item-accordion show">
            <a
              className={`menu-link border-transparent  dark:menu-item-active:bg-coal-300 dark:menu-item-active:border-gray-100 menu-item-active:rounded-lg  dark:hover:bg-coal-300 dark:hover:border-gray-100 hover:rounded-lg gap-[14px] ps-[10px] pe-[10px] py-[8px]
                 ${item.path === location.pathname ? "sidebar-active" : ""}`}
              tabIndex={0}
              onClick={() =>
                hasChildren ? toggleSubMenu(itemKey) : navigate(item.path)
              }
              // onClick={() => {
              //   if (hasChildren) {
              //     toggleSubMenu(itemKey);
              //   } else {
              //     navigate(item.path);
              //     setOpenSubMenu(null); // close submenu
              //     setExpandedSections(null); // close main section
              //     localStorage.removeItem("sidebar_open_submenu");
              //     localStorage.removeItem("sidebar_expanded_section");
              //   }
              // }}
            >
              <span className="menu-bullet flex w-[6px] -start-[3px] rtl:start-0 relative before:absolute before:top-0 before:size-[6px] before:rounded-full rtl:before:translate-x-1/2 before:-translate-y-1/2 menu-item-active:before:bg-primary menu-item-hover:before:bg-primary"></span>
              <span className="menu-title text-sm font-medium side-menu-text menu-item-active:text-primary menu-link-hover">
                {item.label}
              </span>
              {hasChildren && (
                <span className="menu-arrow text-gray-400 w-[20px] shrink-0 justify-end ms-1 me-[-10px]">
                  <i
                    className={`ki-filled ${
                      isOpen ? "ki-minus" : "ki-plus"
                    } text-2xs`}
                  ></i>
                </span>
              )}
            </a>
            {hasChildren && isOpen && (
              <div className="menu-accordion gap-0.5 ps-[10px] relative before:absolute before:start-[20px] before:top-0 before:bottom-0 before:border-s before:border-gray-200 show">
                {(isSuperAdmin
                  ? item.children || []
                  : item.children?.filter((child) =>
                      allowedCodes.includes(child.code)
                    ) || []
                ).map((child, subIdx) => (
                  <div key={subIdx} className="menu-item">
                    <a
                      // className=""
                      className={`menu-link border-transparent  dark:menu-item-active:bg-coal-300 dark:menu-item-active:border-gray-100 menu-item-active:rounded-lg  dark:hover:bg-coal-300 dark:hover:border-gray-100 hover:rounded-lg gap-[14px] ps-[10px] pe-[10px] py-[8px]
                 ${child.path === location.pathname ? "sidebar-active" : ""}`}
                      tabIndex={0}
                      onClick={() => navigate(child.path)}
                    >
                      <span className="menu-bullet flex w-[6px] -start-[3px] rtl:start-0 relative before:absolute before:top-0 before:size-[6px] before:rounded-full rtl:before:translate-x-1/2 before:-translate-y-1/2 menu-item-active:before:bg-primary menu-item-hover:before:bg-primary"></span>
                      <span className="menu-title text-2sm font-normal side-menu-text menu-item-active:text-primary menu-item-active:font-semibold">
                        {child.label}
                      </span>
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  const onPressLogout = () => {
    localStorage.removeItem(msgTypes.ACCESS_TOKEN);
    navigate("/");
  };

  return (
    <div className="flex grow">
      <div
        className="sidebar dark:bg-coal-600 side-menu-bg border-e border-e-gray-200 dark:border-e-coal-100 fixed z-20 hidden lg:flex flex-col items-stretch shrink-0"
        data-drawer="true"
        id="sidebar"
      >
        <div
          className="sidebar-header hidden lg:flex items-center relative justify-between px-3 lg:px-6 shrink-0 pb-2"
          id="sidebar_header"
        >
          <Link className="dark:hidden" to={"/dashboard"}>
            <img
              src="media/images/Yellow-white.png"
              alt="Logo"
              style={{
                width: "117px",
                marginTop: "14px",
                marginLeft: "21px",
              }}
            />
          </Link>
        </div>

        <div
          className="sidebar-content flex grow shrink-0 py-4 pe-4 height-100vh flex-col justify-between scrollable-y-auto"
          id="sidebar_content"
          style={{
            backgroundColor: "#433127",
            height: "93vh",
            minHeight: "93vh",
          }}
        >
          <div
            className=" grow shrink-0 flex ps-4 lg:ps-5 pe-1 lg:pe-3"
            id="sidebar_scrollable"
          >
            <div
              style={{ marginTop: "18px" }}
              className="menu flex flex-col grow gap-3"
              id="sidebar_menu"
            >
              {MENU_CONFIG?.map((section: any, index: number) => {
                const filteredChildren = isSuperAdmin
                  ? section.children
                  : section.children.filter(
                      (child: any) =>
                        allowedCodes.includes(child.code) ||
                        child.children?.some((sub: any) =>
                          allowedCodes.includes(sub.code)
                        )
                    );

                if (!isSuperAdmin && filteredChildren.length === 0) return null;

                const sectionKey = `main-${index}`;
                const isExpanded = expandedSections === sectionKey;

                return (
                  <div key={index}>
                    <div
                      className="menu-section-title text-white text-sm mb-2 font-semibold flex justify-between items-center cursor-pointer"
                      onClick={() => toggleMainSection(sectionKey)}
                    >
                      <span>{section.label}</span>
                      <span className="menu-icon items-start side-menu-text dark:text-gray-400 w-[20px]">
                        <i
                          className={`ki-filled text-white ${
                            isExpanded ? "ki-minus" : "ki-plus"
                          }`}
                        ></i>
                      </span>
                    </div>

                    {isExpanded &&
                      renderMenuItems(filteredChildren, sectionKey)}
                  </div>
                );
              })}
            </div>
          </div>
          {/* ✅ Add this version block */}
          <div className="px-4 py-2 text-white text-xs text-start underline ">
            Version {`${ApiUrl.version}`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SideMenu;
